import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Archery Manager AI</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-500 mb-8">Effective Date: February 2026</p>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 leading-relaxed">
              Welcome to Archery Manager AI ("we," "our," or "us"). By accessing or using our website and application at ArcheryManagerAI.com, you agree to the following Terms of Service.
            </p>
            <p className="text-slate-700 leading-relaxed font-medium">
              If you do not agree with these terms, please do not use the platform.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Purpose of the Platform</h2>
            <p className="text-slate-700 leading-relaxed">
              Archery Manager AI is an AI-assisted platform designed to help archers, parents, coaches, and teams track tournaments, results, schedules, and performance insights.
            </p>
            <p className="text-slate-700 leading-relaxed">
              The platform is provided for informational and organizational purposes only.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. Eligibility & Accounts</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Accounts are created by invitation or approval only</li>
              <li>Users are responsible for maintaining the confidentiality of their login credentials</li>
              <li>Parents or guardians are responsible for accounts created on behalf of minors</li>
              <li>We reserve the right to approve, suspend, or revoke access at our discretion</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. User Responsibilities</h2>
            <p className="text-slate-700 leading-relaxed mb-2">You agree not to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Upload false or misleading information</li>
              <li>Attempt to access systems without authorization</li>
              <li>Reverse engineer or misuse the platform</li>
              <li>Use the platform for unlawful or abusive purposes</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Data Accuracy Disclaimer</h2>
            <p className="text-slate-700 leading-relaxed">
              Archery Manager AI may aggregate data from public sources, user submissions, or third-party inputs.
            </p>
            <p className="text-slate-700 leading-relaxed mb-2">We do not guarantee the accuracy, completeness, or timeliness of:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Tournament schedules</li>
              <li>Results</li>
              <li>Rankings</li>
              <li>Event data</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-2 font-medium">
              Users should always verify official results with governing bodies.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. AI-Generated Insights</h2>
            <p className="text-slate-700 leading-relaxed">
              AI-generated insights are advisory only and should not be treated as professional coaching, medical, or competitive guarantees.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Decisions made based on AI insights are the responsibility of the user.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Intellectual Property</h2>
            <p className="text-slate-700 leading-relaxed">
              All content, software, workflows, and designs on ArcheryManagerAI.com are the property of Archery Manager AI and may not be copied, reproduced, or redistributed without written permission.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Service Availability</h2>
            <p className="text-slate-700 leading-relaxed">
              We strive to maintain availability but do not guarantee uninterrupted service. Features may change or be discontinued as the platform evolves.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Termination</h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to suspend or terminate access if these terms are violated.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-700 leading-relaxed mb-2">Archery Manager AI is provided "as is." We are not liable for:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Data inaccuracies</li>
              <li>Missed events</li>
              <li>Performance outcomes</li>
              <li>Indirect or consequential damages</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Contact</h2>
            <p className="text-slate-700 leading-relaxed">
              For questions about these terms, contact:
            </p>
            <div className="flex items-center gap-2 mt-2 text-primary">
              <Mail className="w-4 h-4" />
              <a href="mailto:support@archerymanagerai.com" className="hover:underline">
                support@archerymanagerai.com
              </a>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <span className="mx-2">•</span>
          <Link to="/login" className="text-primary hover:underline">Back to Login</Link>
        </div>
      </main>
    </div>
  );
}
